import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany, PageHeader, EmptyState, Money, LineItemsEditor, computeTotals } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Loader2, CheckCircle2, PackageCheck, FileText, Wallet } from "lucide-react";

const PO_STATUS = { draft: ["Demande", "bg-slate-100 text-slate-700"], approved: ["Approuvé (BC)", "bg-blue-100 text-blue-700"],
  received: ["Réceptionné", "bg-indigo-100 text-indigo-700"], invoiced: ["Facturé", "bg-amber-100 text-amber-700"], closed: ["Payé", "bg-emerald-100 text-emerald-700"] };

export default function Purchasing() {
  const { companyId, cur } = useCompany();
  const [pos, setPos] = useState(null);
  const [sis, setSis] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ supplier_id: "", lines: [{ product_id: "", name: "", qty: 1, unit_price: 0, tax_rate: 0 }] });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/purchase-orders", { params: { company_id: companyId } }).then(({ data }) => setPos(data.purchase_orders));
    api.get("/supplier-invoices", { params: { company_id: companyId } }).then(({ data }) => setSis(data.supplier_invoices));
  };
  useEffect(() => {
    if (!companyId) return; load();
    api.get("/suppliers", { params: { company_id: companyId } }).then(({ data }) => setSuppliers(data.suppliers));
    api.get("/products", { params: { company_id: companyId } }).then(({ data }) => setProducts(data.products));
  }, [companyId]);

  const totals = computeTotals(form.lines);
  const create = async () => {
    if (!form.supplier_id) return toast.error("Choisissez un fournisseur");
    setSaving(true);
    try { await api.post("/purchase-orders", form, { params: { company_id: companyId } }); toast.success("Bon de commande créé");
      setOpen(false); setForm({ supplier_id: "", lines: [{ product_id: "", name: "", qty: 1, unit_price: 0, tax_rate: 0 }] }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const act = async (id, action) => {
    try { await api.post(`/purchase-orders/${id}/${action}`, {}, { params: { company_id: companyId } });
      toast.success(action === "approve" ? "Approuvé" : action === "receive" ? "Réceptionné (stock mis à jour)" : "Facture fournisseur créée + écriture"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const paySi = async (si) => {
    try { await api.post(`/supplier-invoices/${si.id}/pay`, { amount: si.total - si.amount_paid, method: "bank" }, { params: { company_id: companyId } });
      toast.success("Fournisseur payé + écriture comptable"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="purchasing-page">
      <PageHeader title="Achats" subtitle="Cycle complet : demande → BC → réception → facture → paiement." action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-po-button"><Plus className="w-4 h-4 mr-1" /> Bon de commande</Button></DialogTrigger>
          <DialogContent className="bg-popover max-w-3xl">
            <DialogHeader><DialogTitle className="font-display">Nouveau bon de commande</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1"><Label>Fournisseur *</Label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} data-testid="po-supplier-select">
                  <option value="">— Choisir —</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select></div>
              <LineItemsEditor lines={form.lines} setLines={(l) => setForm({ ...form, lines: l })} products={products} />
              <div className="text-right font-display font-bold">Total <Money value={totals.total} cur={cur} /></div>
            </div>
            <DialogFooter><Button onClick={create} disabled={saving} className="rounded-full" data-testid="save-po-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>} />

      <Tabs defaultValue="po">
        <TabsList className="mb-4"><TabsTrigger value="po" data-testid="tab-po">Bons de commande</TabsTrigger><TabsTrigger value="si" data-testid="tab-si">Factures fournisseurs</TabsTrigger></TabsList>
        <TabsContent value="po">
          <Card className="rounded-md border-border shadow-sm overflow-hidden">
            {pos === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
              : pos.length === 0 ? <EmptyState title="Aucun bon de commande" hint="Créez un fournisseur puis un BC." />
              : <Table>
                  <TableHeader><TableRow><TableHead>N°</TableHead><TableHead>Fournisseur</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>{pos.map((p) => { const [lbl, cls] = PO_STATUS[p.status] || [p.status, ""]; return (
                    <TableRow key={p.id} data-testid={`po-row-${p.id}`}>
                      <TableCell className="font-mono text-xs">{p.number}</TableCell>
                      <TableCell className="font-medium">{p.supplier_name}</TableCell>
                      <TableCell className="text-right"><Money value={p.total} cur={cur} /></TableCell>
                      <TableCell><Badge className={`border-0 ${cls}`}>{lbl}</Badge></TableCell>
                      <TableCell className="text-right space-x-1">
                        {p.status === "draft" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(p.id, "approve")} data-testid={`approve-${p.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approuver</Button>}
                        {p.status === "approved" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(p.id, "receive")} data-testid={`receive-${p.id}`}><PackageCheck className="w-3.5 h-3.5 mr-1" />Réceptionner</Button>}
                        {p.status === "received" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(p.id, "invoice")} data-testid={`invoice-po-${p.id}`}><FileText className="w-3.5 h-3.5 mr-1" />Facturer</Button>}
                      </TableCell>
                    </TableRow>); })}</TableBody>
                </Table>}
          </Card>
        </TabsContent>
        <TabsContent value="si">
          <Card className="rounded-md border-border shadow-sm overflow-hidden">
            {sis.length === 0 ? <EmptyState title="Aucune facture fournisseur" />
              : <Table>
                  <TableHeader><TableRow><TableHead>N°</TableHead><TableHead>Fournisseur</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Payé</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>{sis.map((s) => (
                    <TableRow key={s.id} data-testid={`si-row-${s.id}`}>
                      <TableCell className="font-mono text-xs">{s.number}</TableCell>
                      <TableCell className="font-medium">{s.supplier_name}</TableCell>
                      <TableCell className="text-right"><Money value={s.total} cur={cur} /></TableCell>
                      <TableCell className="text-right text-emerald-600"><Money value={s.amount_paid} cur={cur} /></TableCell>
                      <TableCell className="text-right">{s.status !== "paid" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => paySi(s)} data-testid={`pay-si-${s.id}`}><Wallet className="w-3.5 h-3.5 mr-1" />Payer</Button>}</TableCell>
                    </TableRow>))}</TableBody>
                </Table>}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
